import { useState, useEffect, memo } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import "./NewTaksModal.scss";
import toast from "react-hot-toast";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useLoading } from "../../../shared/context/LoadingContext";
// Mesmo multiselect de utilizadores usado no filtro do fluxo de caixa.
import MultiSelect from "../../financeiro/fluxoCaixa/components/filtros/MultiSelect";

function ModalNewTask({
  open,
  users,
  handleCloseModal,
  atualizarTarefas,
  auth,
  sendRequest,
}) {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Permite delegar a mesma tarefa a vários utilizadores de uma só vez.
  const [delegatarios, setDelegatarios] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [data, setData] = useState(new Date());
  const [descricao, setDescricao] = useState("");
  const [mensagemErro, setMensagemErro] = useState("");

  // O MultiSelect espera opções no formato { id, value }.
  const userOptions = (users || []).map((u) => ({ id: u.id, value: u.name }));

  useEffect(() => {
    // Por omissão a tarefa é para o próprio (comportamento de sempre).
    setDelegatarios([auth.userId]);
  }, [auth.userId]);

  const preSubmitHandler = () => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      submit();
    }
  };

  const submit = async () => {
    // Validação antes de mostrar o loading: o modal tem de continuar aberto
    // para o utilizador ver a mensagem de erro.
    if (!titulo || !descricao || !data) {
      setMensagemErro("Preencha todos os campos antes de prosseguir");
      setIsSubmitting(false);
      return;
    }
    if (!delegatarios || delegatarios.length === 0) {
      setMensagemErro("Escolha pelo menos um utilizador");
      setIsSubmitting(false);
      return;
    }
    setMensagemErro("");

    startLoading();
    try {
      // Cria a mesma tarefa para cada delegatário escolhido. Uma requisição por
      // utilizador mantém o backend intacto (o controller aceita 1 delegatário).
      const criadas = await Promise.all(
        delegatarios.map((delegatarioId) => {
          const formData = new FormData();
          formData.append("delegador", auth.userId);
          formData.append("delegatario", delegatarioId);
          formData.append("titulo", titulo);
          formData.append("descricao", descricao);
          formData.append("data", data);

          return sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/tasks`,
            "POST",
            formData,
            {
              Authorization: "Bearer " + auth.token,
            }
          );
        })
      );

      setTitulo("");
      setDescricao("");
      criadas.forEach((r) => atualizarTarefas(r.task, "adicionar"));
      toast.success(
        criadas.length > 1
          ? `Tarefa adicionada para ${criadas.length} utilizadores`
          : "Tarefa adicionada com sucesso"
      );
      // Só fecha em caso de sucesso — em erro o modal fica aberto com os dados.
      handleCloseModal();
    } catch (err) {
      console.error("err", err);
    } finally {
      // Repor sempre, senão o botão fica bloqueado para o resto da sessão
      // (o modal está sempre montado, o estado sobrevive ao fechar/reabrir).
      setIsSubmitting(false);
      stopLoading();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="fluxo-atendimento__modal__box" id="dividas__modal">
        <h1 className="fluxo-atendimento__modal__titulo">Nova Tarefa</h1>
        <div className="newTaskContainer">
          <div
            className={`customInputContainer ${
              auth.perm.includes("c-tar") ? "takes4" : "takes5"
            }`}
          >
            <input
              type="text"
              onChange={(e) => setTitulo(e.target.value)}
              className="customInput"
              value={titulo}
            />
            <label className="customInputLabel">Título*</label>
          </div>

          {auth.perm.includes("c-tar") && (
            <div className="customInputContainer takes2">
              <div className="task-multiselect-wrap">
                <MultiSelect
                  options={userOptions}
                  selectedIds={delegatarios}
                  onChange={setDelegatarios}
                  placeholder="Selecionar utilizadores"
                />
              </div>
              <label className="customInputLabelSelect">Delegatário(s)*</label>
            </div>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment} className="takes2">
            <DesktopDatePicker
              className="dataNascimento__cliente datepicker-tasks"
              label="Data Execução*"
              inputFormat="DD-MM-YYYY"
              value={data}
              onChange={(value) => setData(value)}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>

          <div className="customInputContainer takesAll">
            <textarea
              rows={5}
              onChange={(e) => setDescricao(e.target.value)}
              className="customInputTextarea"
              value={descricao}
            />
            <label className="customInputLabelSelect">Descrição*</label>
          </div>
        </div>

        {mensagemErro && (
          <div className="fluxo-atendimento__modal__erros">{mensagemErro}</div>
        )}

        <div
          style={{
            gridColumn: "1/-1",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "25px",
          }}
        >
          <button
            type="button"
            disabled={isSubmitting}
            className="blue-button"
            onClick={preSubmitHandler}
          >
            {isSubmitting ? "Guardando..." : "Guardar"}
          </button>

          <span className="cancel-btn" onClick={handleCloseModal}>
            Fechar
          </span>
        </div>
      </Box>
    </Modal>
  );
}

export default memo(ModalNewTask);
